import { useEffect, useMemo, useRef, useState } from 'react';
import type { Photo } from '../types';
import { api } from '../api';
import { GLRenderer } from './engine/GLRenderer';
import { useEditorStore } from '../store/editorStore';
import { defaultEditState, type EditState } from './editState';
import { PanelSection } from '../components/PanelSection';
import { Histogram } from './Histogram';
import { CropPanel } from './panels/CropPanel';
import { CropOverlay, type CropRect } from './CropOverlay';
import { BasicPanel } from './panels/BasicPanel';
import { ColorPanel } from './panels/ColorPanel';
import { ColorGradingPanel } from './panels/ColorGradingPanel';
import { DetailPanel } from './panels/DetailPanel';
import { LensPanel } from './panels/LensPanel';
import { MaskingPanel } from './panels/MaskingPanel';
import { RepairPanel } from './panels/RepairPanel';
import './Editor.css';

interface Props {
  photo: Photo;
  onBack: () => void;
}

// Aspect-ratio presets. `ratio` is the pixel width/height; `null` = free-form
// and `original` matches the source image's own aspect.
const ASPECTS: { key: string; label: string; ratio: number | null }[] = [
  { key: 'free', label: 'Free', ratio: null },
  { key: 'original', label: 'Original', ratio: 0 },
  { key: '1:1', label: '1:1', ratio: 1 },
  { key: '16:9', label: '16:9', ratio: 16 / 9 },
  { key: '9:16', label: '9:16', ratio: 9 / 16 },
  { key: '4:3', label: '4:3', ratio: 4 / 3 },
  { key: '3:4', label: '3:4', ratio: 3 / 4 },
  { key: '3:2', label: '3:2', ratio: 3 / 2 },
  { key: '2:3', label: '2:3', ratio: 2 / 3 },
  { key: '5:4', label: '5:4', ratio: 5 / 4 },
];

// While cropping we show the full, un-transformed image so the crop rectangle
// maps directly onto the source pixels the crop values refer to.
function cropPreviewState(state: EditState): EditState {
  return {
    ...state,
    crop: {
      ...state.crop,
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      angle: 0,
      rotate90: 0,
      flipH: false,
      flipV: false,
    },
  };
}

export function Editor({ photo, onBack }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<GLRenderer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [histVersion, setHistVersion] = useState(0);

  const [cropMode, setCropMode] = useState(false);
  const [cropDraft, setCropDraft] = useState<CropRect>({
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  });
  const [cropAspect, setCropAspect] = useState<{
    key: string;
    norm: number | null;
  }>({ key: 'free', norm: null });
  const [imgAspect, setImgAspect] = useState(1);

  const state = useEditorStore((s) => s.state);
  const load = useEditorStore((s) => s.load);
  const update = useEditorStore((s) => s.update);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const reset = useEditorStore((s) => s.reset);
  const dirty = useEditorStore((s) => s.dirty);
  const markSaved = useEditorStore((s) => s.markSaved);

  const originalState = useMemo(() => defaultEditState(), []);


  // Set up renderer + load image and saved edits.
  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let renderer: GLRenderer;
    try {
      renderer = new GLRenderer(canvas);
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    rendererRef.current = renderer;

    const img = new Image();
    img.decoding = 'async';
    img.onload = async () => {
      if (cancelled) return;
      renderer.setImage(img);
      setImgAspect(img.width / img.height);
      try {
        const settings = await api.getEdits<Record<string, unknown>>(photo.id);
        if (cancelled) return;
        load(settings);
      } catch {
        load(undefined);
      }
      setLoading(false);
    };
    img.onerror = () => setError('Could not load the image.');
    img.src = photo.originalUrl;

    return () => {
      cancelled = true;
      renderer.dispose();
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.id]);

  // Re-render whenever the edit state (or before/after toggle) changes.
  useEffect(() => {
    const r = rendererRef.current;
    if (!r || loading) return;
    const display = showOriginal
      ? originalState
      : cropMode
        ? cropPreviewState(state)
        : state;
    r.render(display);
    const id = requestAnimationFrame(() => setHistVersion((v) => v + 1));
    return () => cancelAnimationFrame(id);
  }, [state, showOriginal, loading, originalState, cropMode]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo]);

  async function save() {
    setSaving(true);
    try {
      await api.saveEdits(photo.id, state);
      markSaved();
    } finally {
      setSaving(false);
    }
  }

  async function exportImage() {
    const r = rendererRef.current;
    if (!r) return;
    r.render(state);
    const blob = await r.toBlob('image/jpeg', 0.92);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = photo.filename.replace(/\.[^.]+$/, '') + '-edited.jpg';
    a.click();
    URL.revokeObjectURL(url);
  }

  function enterCrop() {
    setCropDraft({
      x: state.crop.x,
      y: state.crop.y,
      width: state.crop.width,
      height: state.crop.height,
    });
    setCropAspect({ key: 'free', norm: null });
    setCropMode(true);
  }

  function applyCrop() {
    update((d) => {
      d.crop.x = cropDraft.x;
      d.crop.y = cropDraft.y;
      d.crop.width = cropDraft.width;
      d.crop.height = cropDraft.height;
    });
    setCropMode(false);
  }

  function chooseAspect(key: string, ratio: number | null) {
    if (ratio == null) {
      setCropAspect({ key, norm: null });
      return;
    }
    const pixelRatio = key === 'original' ? imgAspect : ratio;
    const norm = pixelRatio / imgAspect;
    let w: number;
    let h: number;
    if (norm >= 1) {
      w = 1;
      h = 1 / norm;
    } else {
      h = 1;
      w = norm;
    }
    setCropAspect({ key, norm });
    setCropDraft({ x: (1 - w) / 2, y: (1 - h) / 2, width: w, height: h });
  }

  return (
    <div className="editor">
      <header className="editor__bar">
        <button className="chip" onClick={onBack}>
          ← Library
        </button>
        <span className="editor__name">{photo.filename}</span>
        <div className="editor__actions">
          <button
            className={`chip ${cropMode ? 'is-active' : ''}`}
            onClick={() => (cropMode ? setCropMode(false) : enterCrop())}
            title="Crop & aspect ratio"
          >
            Crop
          </button>
          <button
            className="chip"
            onMouseDown={() => setShowOriginal(true)}
            onMouseUp={() => setShowOriginal(false)}
            onMouseLeave={() => setShowOriginal(false)}
            title="Hold to view the original"
          >
            Before/After
          </button>
          <button className="chip" onClick={undo} title="Undo (Ctrl+Z)">
            Undo
          </button>
          <button className="chip" onClick={redo} title="Redo (Ctrl+Shift+Z)">
            Redo
          </button>
          <button className="chip" onClick={reset}>
            Reset
          </button>
          <button className="chip" onClick={exportImage}>
            Export
          </button>
          <button
            className="chip chip--primary"
            onClick={save}
            disabled={saving || !dirty}
          >
            {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </header>

      <div className="editor__body">
        <div className="editor__canvaswrap">
          {error ? (
            <p className="editor__error">{error}</p>
          ) : (
            <canvas ref={canvasRef} className="editor__canvas" />
          )}
          {loading && !error && <p className="editor__loading">Loading…</p>}
          {showOriginal && <span className="editor__badge">Original</span>}
          {cropMode && !error && !loading && (
            <>
              <CropOverlay
                canvasRef={canvasRef}
                rect={cropDraft}
                aspect={cropAspect.norm}
                onChange={setCropDraft}
              />
              <div className="cropbar">
                <div className="cropbar__aspects">
                  {ASPECTS.map((a) => (
                    <button
                      key={a.key}
                      className={`chip ${
                        cropAspect.key === a.key ? 'is-active' : ''
                      }`}
                      onClick={() => chooseAspect(a.key, a.ratio)}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
                <div className="cropbar__done">
                  <button className="chip" onClick={() => setCropMode(false)}>
                    Cancel
                  </button>
                  <button className="chip chip--primary" onClick={applyCrop}>
                    Apply crop
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <aside className="editor__panel">
          <Histogram canvasRef={canvasRef} version={histVersion} />
          <PanelSection title="Crop & Rotate" help="crop">
            <CropPanel />
          </PanelSection>
          <PanelSection title="Basic" help="whiteBalance" defaultOpen>
            <BasicPanel />
          </PanelSection>
          <PanelSection title="Color" help="hsl">
            <ColorPanel />
          </PanelSection>
          <PanelSection title="Color Grading" help="colorGrading">
            <ColorGradingPanel />
          </PanelSection>
          <PanelSection title="Detail" help="sharpening">
            <DetailPanel />
          </PanelSection>
          <PanelSection title="Lens & Geometry" help="lensCorrections">
            <LensPanel />
          </PanelSection>
          <PanelSection title="Masking" help="masking" badge="beta">
            <MaskingPanel />
          </PanelSection>
          <PanelSection title="Healing & Clone" help="heal" badge="beta">
            <RepairPanel />
          </PanelSection>
        </aside>
      </div>
    </div>
  );
}
